import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  setDoc,
  query,
  collection,
  where,
  getDocs,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
} from "firebase/auth";
import { auth, db } from "../firebase";

// Importera bilderna
import appStoreImage from "../images/appstore.png";
import googlePlayImage from "../images/googleplay.png";
import checkImage from "../images/check.png";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tokenValid, setTokenValid] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [companyAccountUid, setCompanyAccountUid] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sanitizeEmailToDocId = (email = "") => {
    return email.trim().toLowerCase().replace(/[.#$/[\]]/g, "_");
  };

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("Ingen inbjudningslänk hittades.");
        setLoading(false);
        return;
      }

      try {
        console.log("=== VERIFIERAR TOKEN ===");

        const tokenRef = doc(db, "invitationTokens", token);
        const tokenDoc = await getDoc(tokenRef);

        if (!tokenDoc.exists()) {
          setError("Inbjudningslänken är ogiltig. Token finns inte i databasen.");
          setLoading(false);
          return;
        }

        const data = tokenDoc.data();

        if (data.used) {
          setError("Denna inbjudan har redan använts.");
          setLoading(false);
          return;
        }

        const expiresAt = data.expiresAt?.toDate
          ? data.expiresAt.toDate()
          : new Date(data.expiresAt);

        if (expiresAt < new Date()) {
          setError("Inbjudningslänken har gått ut. Den var endast giltig i 24 timmar.");
          setLoading(false);
          return;
        }

        let resolvedCompanyAccountUid = "";

        if (data.invitedBy) {
          resolvedCompanyAccountUid = data.invitedBy;
        } else if (data.companyUserId) {
          resolvedCompanyAccountUid = data.companyUserId;
        } else if (data.companyId) {
          const usersQuery = query(
            collection(db, "users"),
            where("companyId", "==", data.companyId)
          );
          const usersSnap = await getDocs(usersQuery);

          if (!usersSnap.empty) {
            resolvedCompanyAccountUid = usersSnap.docs[0].id;
          }
        }

        if (!resolvedCompanyAccountUid) {
          setError("Kunde inte hitta företagets konto. Kontakta administratören.");
          setLoading(false);
          return;
        }

        const companyAccountRef = doc(db, "users", resolvedCompanyAccountUid);
        const companyAccountSnap = await getDoc(companyAccountRef);

        if (!companyAccountSnap.exists()) {
          setError("Kunde inte hitta företagets kontoinformation.");
          setLoading(false);
          return;
        }

        const companyData = companyAccountSnap.data();

        if (!companyData.companyId) {
          setError("Företagets ID saknas. Kontakta administratören.");
          setLoading(false);
          return;
        }

        const invitationPayload = {
          ...data,
          companyAccountUid: resolvedCompanyAccountUid,
          companyId: companyData.companyId,
          companyName: companyData.companyName || data.companyName || "Företag",
        };

        setInvitationData(invitationPayload);
        setCompanyAccountUid(resolvedCompanyAccountUid);
        setTokenValid(true);
        setLoading(false);

        console.log("✅ Token verifierad!");
      } catch (err) {
        console.error("Fel vid verifiering av token:", err);
        setError("Kunde inte verifiera inbjudan. " + err.message);
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (!invitationData) {
      setError("Inbjudningsdata saknas.");
      return;
    }

    if (!companyAccountUid) {
      setError("Företagskonto kunde inte identifieras.");
      return;
    }

    if (!invitationData.companyId) {
      setError("Företags-ID saknas. Kontakta administratören.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Lösenorden matchar inte.");
      return;
    }

    if (password.length < 6) {
      setError("Lösenordet måste vara minst 6 tecken långt.");
      return;
    }

    setIsSubmitting(true);

    const normalizedEmail = invitationData.email?.trim().toLowerCase() || "";
    const emailSafeId = sanitizeEmailToDocId(normalizedEmail);
    const employeeRef = doc(db, "users", companyAccountUid, "employed", emailSafeId);

    const baseUserData = {
      email: normalizedEmail,
      firstName: invitationData.firstName || "",
      lastName: invitationData.lastName || "",
      name: `${invitationData.firstName || ""} ${invitationData.lastName || ""}`.trim(),
      role: invitationData.role || "user",
      personalNumber: invitationData.personalNumber || "–",
      employmentNumber: invitationData.employmentNumber || "–",
      workTimeId: invitationData.workTimeId || "",
      groupId: invitationData.groupId || "",
      companyId: invitationData.companyId,
      companyName: invitationData.companyName,
      companyAccountUid,
      membershipStatus: "active",
      isActive: true,
      updatedAt: serverTimestamp(),
      invitedBy: invitationData.invitedBy || companyAccountUid,
    };

    try {
      console.log("=== STARTAR REGISTRERING ===");

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );

      const user = userCredential.user;

      await updateProfile(user, {
        displayName: `${invitationData.firstName || ""} ${invitationData.lastName || ""}`.trim(),
      });

      const userDocData = {
        uid: user.uid,
        ...baseUserData,
        createdAt: serverTimestamp(),
        registeredAt: serverTimestamp(),
      };

      await setDoc(doc(db, "users", user.uid), userDocData);

      await setDoc(
        employeeRef,
        {
          ...userDocData,
          personalNumber: invitationData.personalNumber || "–",
          registeredUserId: user.uid,
          authUid: user.uid,
          authAccountExists: true,
          invitedEmailMatchedExistingAuth: false,
        },
        { merge: true }
      );

      await updateDoc(doc(db, "invitationTokens", token), {
        used: true,
        usedAt: serverTimestamp(),
        usedBy: user.uid,
        usedByEmail: normalizedEmail,
        linkedToExistingAuthUser: false,
      });

      try {
        await signOut(auth);
      } catch (signOutError) {
        // Registreringen ar redan klar. Ett utloggningsfel ska inte dolja
        // bekräftelsen eller knapparna for att ladda ner appen.
        console.warn("Kunde inte logga ut efter registrering:", signOutError);
      }

      setRegistrationSuccess(true);
      setSuccessMessage("Registrering lyckades!");
      setShowSuccessPopup(true);
    } catch (err) {
      console.error("Registreringsfel:", err);

      if (err.code === "auth/email-already-in-use") {
        try {
          await setDoc(
            employeeRef,
            {
              ...baseUserData,
              personalNumber: invitationData.personalNumber || "–",
              createdAt: serverTimestamp(),
              registeredAt: serverTimestamp(),
              registeredUserId: null,
              authUid: null,
              authAccountExists: true,
              invitedEmailMatchedExistingAuth: true,
            },
            { merge: true }
          );

          await updateDoc(doc(db, "invitationTokens", token), {
            used: true,
            usedAt: serverTimestamp(),
            usedByEmail: normalizedEmail,
            linkedToExistingAuthUser: true,
          });

          setRegistrationSuccess(true);
          setSuccessMessage("Registrering lyckades!");
          setShowSuccessPopup(true);
        } catch (innerErr) {
          console.error("Fel när employed skulle skapas:", innerErr);
          setError(
            "E-postadressen finns redan, men kunde inte läggas till i företaget: " +
              innerErr.message
          );
        }
      } else {
        setError(err.message || "Ett oväntat fel uppstod.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <div>Verifierar inbjudan...</div>
      </div>
    );
  }

  if (!tokenValid || error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#f9fafb",
        }}
      >
        <div
          style={{
            maxWidth: 500,
            width: "100%",
            padding: 24,
            textAlign: "center",
            background: "white",
            borderRadius: 16,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ color: "#dc2626", marginBottom: 12 }}>Inbjudan ogiltig</h2>
          <p>{error}</p>

          <button
            onClick={() => navigate("/login")}
            style={{
              marginTop: 20,
              padding: "10px 20px",
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Gå till inloggning
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#f3f4f6",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            margin: 20,
            padding: 32,
            background: "white",
            borderRadius: 16,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <h2 style={{ fontSize: 28, marginBottom: 8 }}>Skapa konto</h2>
          <p style={{ color: "#6b7280", marginBottom: 24 }}>
            Välkommen till {invitationData?.companyName}
          </p>

          <div
            style={{
              background: "#f3f4f6",
              padding: 16,
              borderRadius: 8,
              marginBottom: 24,
            }}
          >
            <p>
              <strong>Namn:</strong> {invitationData?.firstName} {invitationData?.lastName}
            </p>
            <p>
              <strong>E-post:</strong> {invitationData?.email}
            </p>
            <p>
              <strong>Roll:</strong>{" "}
              {invitationData?.role === "admin"
                ? "Admin"
                : invitationData?.role === "super-admin"
                ? "Super-admin"
                : "Användare"}
            </p>
            <p>
              <strong>Företag:</strong> {invitationData?.companyName}
            </p>
          </div>

          <form onSubmit={handleRegister}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Lösenord</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Minst 6 tecken"
                style={{
                  width: "100%",
                  padding: 12,
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", marginBottom: 6 }}>
                Bekräfta lösenord
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: 12,
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  boxSizing: "border-box",
                }}
              />
            </div>

            {error && (
              <div
                style={{
                  background: "#fee2e2",
                  color: "#991b1b",
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: "100%",
                padding: 12,
                background: isSubmitting ? "#93c5fd" : "#2563eb",
                color: "white",
                border: "none",
                borderRadius: 8,
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
            >
              {isSubmitting ? "Behandlar..." : "Registrera dig"}
            </button>
          </form>
        </div>
      </div>

      {/* Uppdaterad popup med check.png och större text */}
      {showSuccessPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 420,
              margin: 20,
              background: "white",
              borderRadius: 20,
              padding: 40,
              textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              animation: "slideUp 0.3s ease-out",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Dekorativ topplinje */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: "linear-gradient(90deg, #2563eb, #3b82f6)",
              }}
            />

            {/* Check-bild istället för emoji */}
            <div
              style={{
                marginBottom: 20,
                animation: "bounceIn 0.5s ease-out",
              }}
            >
              <img
                src={checkImage}
                alt="Success"
                style={{
                  width: 80,
                  height: 80,
                  display: "block",
                  margin: "0 auto",
                }}
              />
            </div>

            {/* Stor text - "Registrering lyckades!" */}
            <h3
              style={{
                fontSize: 32,
                fontWeight: 700,
                marginBottom: 8,
                color: "#1f2937",
                letterSpacing: "-0.5px",
                lineHeight: 1.2,
              }}
            >
              {successMessage}
            </h3>

            {/* Separator */}
            <div
              style={{
                height: 1,
                background: "#e5e7eb",
                marginBottom: 24,
                marginTop: 16,
              }}
            />

            {/* Ladda ner appen sektion */}
            <div>
              <p
                style={{
                  marginBottom: 20,
                  fontWeight: 600,
                  color: "#374151",
                  fontSize: 15,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                📱 Ladda ner appen
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                }}
              >
                {/* App Store knapp */}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open("https://apps.apple.com/se/app/lunero/id6760931401", "_blank");
                  }}
                  style={{
                    display: "block",
                    width: "180px",
                    cursor: "pointer",
                    transition: "transform 0.2s ease",
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <img
                    src={appStoreImage}
                    alt="App Store"
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                    }}
                  />
                </a>

                {/* Google Play knapp */}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open("https://play.google.com/store/apps/details?id=se.lunero.app&pcampaignid=web_share", "_blank");
                  }}
                  style={{
                    display: "block",
                    width: "180px",
                    cursor: "pointer",
                    transition: "transform 0.2s ease",
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <img
                    src={googlePlayImage}
                    alt="Google Play"
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                    }}
                  />
                </a>
              </div>
            </div>
          </div>

          {/* CSS Animationer */}
          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            @keyframes slideUp {
              from {
                transform: translateY(30px);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            @keyframes bounceIn {
              0% {
                transform: scale(0.5);
                opacity: 0;
              }
              60% {
                transform: scale(1.1);
              }
              100% {
                transform: scale(1);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
